import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  GlossaryTermWithRelations, 
  GlossarySearchParams,
  CreateGlossaryTermRequest,
  UpdateGlossaryTermRequest,
  RelatedTerm
} from '@shared/types/glossary';
import { api, ApiResponse } from '@/services/api';

interface GlossaryState {
  // Modal state
  isModalOpen: boolean;
  
  // Terms data
  terms: GlossaryTermWithRelations[];
  total: number;
  hasMore: boolean;
  
  // Search state
  searchQuery: string;
  selectedTags: string[];
  availableTags: string[];
  
  // UI state
  loading: boolean;
  error: string | null;
  
  // Admin state
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  
  // Current term for viewing/editing
  selectedTerm: GlossaryTermWithRelations | null;
}

const initialState: GlossaryState = {
  isModalOpen: false,
  terms: [],
  total: 0,
  hasMore: false,
  searchQuery: '',
  selectedTags: [],
  availableTags: [],
  loading: false,
  error: null,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  selectedTerm: null
};

// Thunks
export const fetchGlossaryTerms = createAsyncThunk(
  'glossary/fetchTerms',
  async (params: GlossarySearchParams = {}) => {
    const response: ApiResponse<any> = await api.get('/glossary', { params });
    // The API response structure is: response.data = { terms: [], total: number, hasMore: boolean }
    // Not response.data.data - that was the bug!
    return response.data;
  }
);

export const searchGlossaryTerms = createAsyncThunk(
  'glossary/searchTerms',
  async (query: string) => {
    const response: ApiResponse<any> = await api.get('/glossary/search', { params: { q: query } });
    return response.data;
  }
);

export const fetchGlossaryTags = createAsyncThunk(
  'glossary/fetchTags',
  async () => {
    const response: ApiResponse<any> = await api.get('/glossary/tags');
    // The backend returns: { success: true, data: string[] } - so return response.data
    return response.data;
  }
);

export const createGlossaryTerm = createAsyncThunk(
  'glossary/createTerm',
  async (data: CreateGlossaryTermRequest) => {
    const response: ApiResponse<any> = await api.post('/glossary', data);
    return response.data;
  }
);

export const updateGlossaryTerm = createAsyncThunk(
  'glossary/updateTerm',
  async ({ id, data }: { id: string; data: UpdateGlossaryTermRequest }) => {
    const response: ApiResponse<any> = await api.put(`/glossary/${id}`, data);
    return response.data;
  }
);

export const deleteGlossaryTerm = createAsyncThunk(
  'glossary/deleteTerm',
  async (id: string) => {
    await api.delete(`/glossary/${id}`);
    return id;
  }
);

export const fetchGlossaryTerm = createAsyncThunk(
  'glossary/fetchTerm',
  async (id: string) => {
    const response: ApiResponse<any> = await api.get(`/glossary/${id}`);
    return response.data.data;
  }
);

const glossarySlice = createSlice({
  name: 'glossary',
  initialState,
  reducers: {
    // Modal actions
    openGlossaryModal: (state) => {
      state.isModalOpen = true;
      state.error = null;
    },
    
    closeGlossaryModal: (state) => {
      state.isModalOpen = false;
      state.selectedTerm = null;
    },
    
    // Search actions
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    
    setSelectedTags: (state, action: PayloadAction<string[]>) => {
      state.selectedTags = action.payload;
    },
    
    toggleTag: (state, action: PayloadAction<string>) => {
      const tag = action.payload;
      if (state.selectedTags.includes(tag)) {
        state.selectedTags = state.selectedTags.filter(t => t !== tag);
      } else {
        state.selectedTags.push(tag);
      }
    },
    
    clearSearch: (state) => {
      state.searchQuery = '';
      state.selectedTags = [];
    },
    
    // Term selection
    setSelectedTerm: (state, action: PayloadAction<GlossaryTermWithRelations | null>) => {
      state.selectedTerm = action.payload;
    },
    
    // Clear errors
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch terms
    builder
      .addCase(fetchGlossaryTerms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGlossaryTerms.fulfilled, (state, action) => {
        state.loading = false;
        // Add safety checks for payload structure
        if (action.payload && typeof action.payload === 'object') {
          state.terms = action.payload.terms || [];
          state.total = action.payload.total || 0;
          state.hasMore = action.payload.hasMore || false;
        } else {
          // Fallback if payload structure is unexpected
          state.terms = [];
          state.total = 0;
          state.hasMore = false;
        }
      })
      .addCase(fetchGlossaryTerms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch glossary terms';
      })
      
      // Fetch tags
      .addCase(fetchGlossaryTags.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchGlossaryTags.fulfilled, (state, action) => {
        state.availableTags = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchGlossaryTags.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch tags';
      })
      
      // Create term
      .addCase(createGlossaryTerm.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createGlossaryTerm.fulfilled, (state, action) => {
        state.isCreating = false;
        state.terms.unshift(action.payload);
        state.total += 1;
      })
      .addCase(createGlossaryTerm.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.error.message || 'Failed to create term';
      })
      
      // Update term
      .addCase(updateGlossaryTerm.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateGlossaryTerm.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.terms.findIndex((term: any) => term.id === action.payload.id);
        if (index !== -1) {
          state.terms[index] = action.payload;
        }
        if (state.selectedTerm?.id === action.payload.id) {
          state.selectedTerm = action.payload;
        }
      })
      .addCase(updateGlossaryTerm.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.error.message || 'Failed to update term';
      })
      
      // Delete term
      .addCase(deleteGlossaryTerm.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteGlossaryTerm.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.terms = state.terms.filter((term: any) => term.id !== action.payload);
        state.total -= 1;
        if (state.selectedTerm?.id === action.payload) {
          state.selectedTerm = null;
        }
      })
      .addCase(deleteGlossaryTerm.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.error.message || 'Failed to delete term';
      })
      
      // Fetch single term
      .addCase(fetchGlossaryTerm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGlossaryTerm.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTerm = action.payload;
      })
      .addCase(fetchGlossaryTerm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch term';
      });
  }
});

export const {
  openGlossaryModal,
  closeGlossaryModal,
  setSearchQuery,
  setSelectedTags,
  toggleTag,
  clearSearch,
  setSelectedTerm,
  clearError
} = glossarySlice.actions;

export default glossarySlice.reducer;